import { afterNextRender, ChangeDetectionStrategy, Component, ElementRef, inject, OnDestroy, ViewEncapsulation } from '@angular/core';

@Component({
  selector: 'ideal-punchout-promo-ribbon',
  standalone: true,
  template: '',
  styleUrl: './punchout-promo-ribbon.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: { style: 'display: none' },
})
export class PunchoutPromoRibbonComponent implements OnDestroy {
  readonly #el = inject(ElementRef<HTMLElement>);
  #observer: MutationObserver | null = null;
  #badges = new Set<HTMLElement>();

  constructor() {
    afterNextRender(() => this.#attachBadges());
  }

  ngOnDestroy(): void {
    this.#observer?.disconnect();
    this.#observer = null;
    this.#badges.forEach((badge) => badge.remove());
    this.#badges.clear();
  }

  #attachBadges(): void {
    const parent = (this.#el.nativeElement as HTMLElement).parentElement;
    if (!parent) return;

    this.#syncBadges(parent);

    // Watch the whole subtree so badges are (re)inserted as prices are added or
    // reset (e.g. innerHTML replaced on price updates).
    this.#observer = new MutationObserver(() => this.#syncBadges(parent));
    this.#observer.observe(parent, { childList: true, subtree: true });
  }

  #syncBadges(parent: HTMLElement): void {
    // There can be multiple ideal-item-price elements, each with one or more
    // .sale-regular-price blocks. Add a badge to every one that lacks it.
    const saleRegularPrices = parent.querySelectorAll('ideal-item-price .sale-regular-price');

    saleRegularPrices.forEach((saleRegularPrice) => {
      if (saleRegularPrice.querySelector(':scope > .punchout-promo-badge')) return;

      const currencySpan = saleRegularPrice.querySelector('span.currency');
      if (!currencySpan) return;

      this.#insert(saleRegularPrice, currencySpan);
    });
  }

  #insert(saleRegularPrice: Element, before: Element): void {
    const badge = document.createElement('span');
    badge.className = 'punchout-promo-badge';
    badge.title = $localize`:@@product.punchout-promo.tooltip:5% punchout catalog discount applied`;
    badge.textContent = $localize`:@@product.punchout-promo.label:5% Off`;
    saleRegularPrice.insertBefore(badge, before);
    this.#badges.add(badge);
  }
}
